if (!customElements.get('customer-reviews')) {
class CustomerReviews extends HTMLElement {
  static feedRequests = new Map();

  static getReviewData(url) {
    if (!this.feedRequests.has(url)) {
      this.feedRequests.set(url, this.fetchReviewData(url).catch((error) => {
        this.feedRequests.delete(url);
        throw error;
      }));
    }
    return this.feedRequests.get(url);
  }

  static async fetchReviewData(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Unable to load reviews: ${response.status}`);
    return response.json();
  }

  constructor() {
    super();

    this.pageSize = Number.parseInt(this.dataset.pageSize, 10) || 5;
    this.currentPage = 1;
    this.reviews = [];
  }

  connectedCallback() {
    if (this.dataset.initialised === 'true') {
      return;
    }

    this.dataset.initialised = 'true';
    this.cacheElements();
    this.loadReviews();

    // The list can't be measured while collapsed, so size it once it opens.
    this.all?.addEventListener('toggle', () => {
      if (this.all.open) this.setStableListHeight();
    });
  }

  cacheElements() {
    this.status = this.querySelector('[data-reviews-status]');
    this.photosRegion = this.querySelector('[data-reviews-photos-region]');
    this.photos = this.querySelector('[data-reviews-photos]');
    this.list = this.querySelector('[data-reviews-list]');
    this.pagination = this.querySelector('[data-reviews-pagination]');
    this.all = this.querySelector('[data-reviews-all]');
  }

  async loadReviews() {
    try {
      const data = await CustomerReviews.getReviewData(this.dataset.reviewsUrl);
      const reviews = Array.isArray(data.reviews) ? data.reviews : [];
      this.reviews = [...reviews].sort((first, second) => second.review_date.localeCompare(first.review_date));

      this.renderPhotos();
      this.setStableListHeight();
      this.renderReviews();
      this.setStatus('');

      document.fonts?.ready.then(() => {
        this.setStableListHeight();
      });
    } catch (error) {
      this.renderError();
      console.error(error);
    }
  }

  renderPhotos() {
    if (!this.photosRegion || !this.photos || this.dataset.showPhotos !== 'true') {
      return;
    }

    const pictureUrls = this.reviews
      .flatMap((review) => (Array.isArray(review.picture_urls) ? review.picture_urls : []))
      .filter(Boolean)
      .slice(0, 12);

    if (pictureUrls.length === 0) {
      this.photosRegion.hidden = true;
      return;
    }

    this.photos.replaceChildren();

    pictureUrls.forEach((url, index) => {
      const link = document.createElement('a');
      link.className = 'customer-reviews__photo-link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      const image = document.createElement('img');
      image.className = 'customer-reviews__photo';
      image.src = url;
      image.alt = `Customer review photo ${index + 1}`;
      image.loading = 'lazy';
      image.width = 320;
      image.height = 320;

      link.append(image);
      this.photos.append(link);
    });

    this.photosRegion.hidden = false;
  }

  renderReviews() {
    if (!this.list) {
      return;
    }

    if (this.reviews.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'customer-reviews__empty';
      empty.textContent = 'No reviews yet.';
      this.list.replaceChildren(empty);
      this.renderPagination();
      return;
    }

    const startIndex = (this.currentPage - 1) * this.pageSize;
    const reviewsToShow = this.reviews.slice(startIndex, startIndex + this.pageSize);
    const fragment = document.createDocumentFragment();

    reviewsToShow.forEach((review) => {
      fragment.append(this.createReview(review));
    });

    this.list.replaceChildren(fragment);
    this.renderPagination();
  }

  setStableListHeight() {
    if (!this.list || this.reviews.length === 0) {
      return;
    }

    const listWidth = this.list.getBoundingClientRect().width;

    if (listWidth === 0) {
      return;
    }

    const probe = document.createElement('div');
    probe.className = 'customer-reviews__list customer-reviews__list--height-probe';
    probe.setAttribute('aria-hidden', 'true');
    probe.style.width = `${listWidth}px`;

    this.list.after(probe);

    const totalPages = Math.ceil(this.reviews.length / this.pageSize);
    let tallestPageHeight = 0;

    for (let page = 1; page <= totalPages; page += 1) {
      const startIndex = (page - 1) * this.pageSize;
      const pageReviews = this.reviews.slice(startIndex, startIndex + this.pageSize);
      const fragment = document.createDocumentFragment();

      pageReviews.forEach((review) => {
        fragment.append(this.createReview(review));
      });

      probe.replaceChildren(fragment);
      tallestPageHeight = Math.max(tallestPageHeight, probe.scrollHeight);
    }

    probe.remove();

    if (tallestPageHeight > 0) {
      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const listHeightRem = Math.ceil((tallestPageHeight / rootFontSize) * 100) / 100;

      this.style.setProperty('--customer-reviews-list-min-height', `${listHeightRem}rem`);
    }
  }

  renderPagination() {
    if (!this.pagination) {
      return;
    }

    const totalPages = Math.ceil(this.reviews.length / this.pageSize);
    this.pagination.replaceChildren();

    if (totalPages <= 1) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.append(
      this.createPaginationButton('Previous', this.currentPage - 1, this.currentPage === 1)
    );

    this.getVisiblePages(totalPages).forEach((page) => {
      if (page === 'ellipsis') {
        const ellipsis = document.createElement('span');
        ellipsis.className = 'customer-reviews__pagination-ellipsis';
        ellipsis.textContent = '...';
        ellipsis.setAttribute('aria-hidden', 'true');
        this.pagination.append(ellipsis);
        return;
      }

      this.pagination.append(this.createPaginationButton(String(page), page, false, page));
    });

    this.pagination.append(
      this.createPaginationButton('Next', this.currentPage + 1, this.currentPage === totalPages)
    );
    this.pagination.hidden = false;
  }

  createPaginationButton(label, page, disabled = false, pageNumber = null) {
    const button = document.createElement('button');
    button.className = 'customer-reviews__pagination-button';
    button.type = 'button';
    button.textContent = label;
    button.disabled = disabled;

    if (pageNumber === this.currentPage) {
      button.classList.add('customer-reviews__pagination-button--current');
      button.setAttribute('aria-current', 'page');
    }

    if (pageNumber) {
      button.setAttribute('aria-label', `Go to reviews page ${pageNumber}`);
    }

    button.addEventListener('click', () => {
      this.currentPage = page;
      this.renderReviews();
      this.list?.focus({ preventScroll: true });
    });

    return button;
  }

  getVisiblePages(totalPages) {
    const pages = new Set([1, totalPages]);

    for (let page = this.currentPage - 1; page <= this.currentPage + 1; page += 1) {
      if (page > 1 && page < totalPages) {
        pages.add(page);
      }
    }

    return [...pages]
      .sort((first, second) => first - second)
      .reduce((visiblePages, page, index, sortedPages) => {
        if (index > 0 && page - sortedPages[index - 1] > 1) {
          visiblePages.push('ellipsis');
        }

        visiblePages.push(page);
        return visiblePages;
      }, []);
  }

  createReview(review) {
    const article = document.createElement('article');
    article.className = 'customer-reviews__review';

    const reviewer = document.createElement('div');
    reviewer.className = 'customer-reviews__reviewer';

    const verified = document.createElement('span');
    verified.className = 'customer-reviews__verified';

    const verifiedIcon = document.createElement('span');
    verifiedIcon.className = 'customer-reviews__verified-icon';
    verifiedIcon.setAttribute('aria-hidden', 'true');
    verifiedIcon.append(this.createVerifiedIcon());

    const verifiedText = document.createElement('span');
    verifiedText.textContent = 'Verified Customer';

    verified.append(verifiedIcon, verifiedText);

    const name = document.createElement('p');
    name.className = 'customer-reviews__name';
    name.textContent = review.reviewer_name || 'Anonymous';

    reviewer.append(verified, name);

    const formattedDate = this.formatDate(review.review_date);

    if (formattedDate) {
      const date = document.createElement('time');
      date.className = 'customer-reviews__date';
      date.dateTime = review.review_date;
      date.textContent = formattedDate;
      reviewer.append(date);
    }

    const content = document.createElement('div');
    content.className = 'customer-reviews__content';

    const stars = document.createElement('div');
    stars.className = 'customer-reviews__stars';
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    stars.style.setProperty(
      '--customer-reviews-stars-fill',
      `${Math.min(100, Math.max(0, (Number(review.rating) / 5) * 100))}%`
    );
    stars.append(this.createStarLayer('customer-reviews__stars-base'));
    stars.append(this.createStarLayer('customer-reviews__stars-fill'));

    content.append(stars);

    if (review.title) {
      const title = document.createElement('h3');
      title.className = 'customer-reviews__title';
      title.textContent = review.title;
      content.append(title);
    }

    if (review.body) {
      const body = document.createElement('p');
      body.className = 'customer-reviews__body';
      body.textContent = review.body;
      content.append(body);
    }

    article.append(reviewer, content);

    return article;
  }

  createVerifiedIcon() {
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('focusable', 'false');

    const burst = document.createElementNS(namespace, 'path');
    burst.setAttribute(
      'd',
      'M12 1.75l2.09 2.15 2.89-.72.94 2.83 2.9.74-.75 2.9 2.18 2.05-2.18 2.06.75 2.9-2.9.73-.94 2.83-2.89-.72L12 21.65 9.91 19.5l-2.89.72-.94-2.83-2.9-.73.75-2.9-2.18-2.06 2.18-2.05-.75-2.9 2.9-.74.94-2.83 2.89.72L12 1.75z'
    );

    const check = document.createElementNS(namespace, 'path');
    check.setAttribute('d', 'M7.75 12.13l2.55 2.55 5.95-6.08');
    check.setAttribute('fill', 'none');
    check.setAttribute('stroke-linecap', 'round');
    check.setAttribute('stroke-linejoin', 'round');
    check.setAttribute('stroke-width', '2.15');

    svg.append(burst, check);

    return svg;
  }

  createStarLayer(className) {
    const span = document.createElement('span');
    span.className = className;
    span.setAttribute('aria-hidden', 'true');
    span.textContent = '\u2605\u2605\u2605\u2605\u2605';

    return span;
  }

  setStatus(message) {
    if (!this.status) {
      return;
    }

    this.status.textContent = message;
    this.status.hidden = message === '';
  }

  renderError() {
    this.setStatus('Reviews are unavailable right now.');

    if (this.list) {
      const empty = document.createElement('p');
      empty.className = 'customer-reviews__empty';
      empty.textContent = 'Reviews are unavailable right now.';
      this.list.replaceChildren(empty);
    }

    this.renderPagination();
  }

  formatDate(value) {
    if (!value) {
      return '';
    }

    const date = new Date(`${value}T00:00:00`);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }
}

  customElements.define('customer-reviews', CustomerReviews);
}
